import { AppSidebarMenuItem } from '@monetize/types/app';
import {
  HistoryEduIcon,
  ReportChartIcon,
  RevenueIcon,
} from '@monetize/ui/icons';
import { useMemo } from 'react';
import { FaMoneyBillTrendUp } from 'react-icons/fa6';
import {
  MdAccountBalance,
  MdDashboard,
  MdFormatQuote,
  MdSettings,
} from 'react-icons/md';
import { useRevRecSettings } from '../api/revenueService';
import { ROLES, ROUTES } from '../constants';
import { useFlags } from '../services/launchDarkly';

export const useNavMenuItems = (): AppSidebarMenuItem[] => {
  const { metabaseReporting, revenueRecognition } = useFlags();
  const { data: recRevSettings } = useRevRecSettings();

  return useMemo(
    () => [
      {
        label: 'Dashboard',
        icon: MdDashboard,
        to: ROUTES.DASHBOARD,
        activeWhen: [{ op: 'startsWith' as const, route: ROUTES.DASHBOARD }],
        roles: ROLES.DASHBOARD_ROLES,
      },
      {
        label: 'Accounts',
        icon: MdAccountBalance,
        to: ROUTES.ACCOUNT_LIST,
        activeWhen: [
          { op: 'startsWith' as const, route: ROUTES.ACCOUNT_LIST },
          { op: 'startsWith' as const, route: '/invoices/' },
          { op: 'startsWith' as const, route: '/bill-groups/' },
        ],
        roles: ROLES.ACCOUNTS_ROLES,
      },
      {
        label: 'Sales',
        icon: MdFormatQuote,
        to: ROUTES.SALES_QUOTES_ROUTE,
        activeWhen: [
          { op: 'startsWith' as const, route: ROUTES.SALES_LANDING },
          { op: 'startsWith' as const, route: ROUTES.QUOTE_BASE_PATH },
          { op: 'startsWith' as const, route: '/contracts/' },
        ],
        roles: ROLES.QUOTES_ROLES,
      },
      {
        label: 'Billing',
        icon: FaMoneyBillTrendUp,
        to: ROUTES.BILLING_BILL_RUN,
        activeWhen: [{ op: 'startsWith' as const, route: ROUTES.BILLING }],
        roles: ROLES.BILLING_ADMIN_ROLES,
      },
      {
        label: 'Product Catalog',
        icon: HistoryEduIcon,
        to: ROUTES.PRODUCT_LIST,
        activeWhen: [
          { op: 'startsWith' as const, route: ROUTES.PRODUCT_LIST },
          { op: 'startsWith' as const, route: ROUTES.ONETIME_LIST },
          { op: 'startsWith' as const, route: ROUTES.OFFERING_LIST },
          { op: 'startsWith' as const, route: ROUTES.DISCOUNT_LIST },
          { op: 'startsWith' as const, route: ROUTES.USAGE_TYPE_LIST },
          { op: 'startsWith' as const, route: ROUTES.BUCKET_LIST },
        ],
        roles: ROLES.PROD_CATALOG_ROLES,
      },
      {
        label: 'Reporting',
        icon: ReportChartIcon,
        to: ROUTES.REPORTING,
        activeWhen: [{ op: 'startsWith' as const, route: ROUTES.REPORTING }],
        /** Only show when feature flag is enabled */
        roles: metabaseReporting ? ROLES.REPORTING_ROLES : [],
      },
      {
        label: 'Revenue',
        icon: RevenueIcon,
        to: ROUTES.REVENUE,
        activeWhen: [{ op: 'startsWith' as const, route: ROUTES.REVENUE }],
        roles: ROLES.REVENUE_ROLES,
        hidden: !recRevSettings?.enabled || !revenueRecognition,
      },
      {
        label: 'Settings',
        icon: MdSettings,
        to: ROUTES.SETTINGS,
        activeWhen: [{ op: 'startsWith' as const, route: ROUTES.SETTINGS }],
        roles: ROLES.SETTINGS_ROLES,
      },
    ],
    [metabaseReporting, recRevSettings, revenueRecognition],
  );
};
